/**
 * Help nudges (BACKLOG #46, nudges 2-4). Small pink bubbles that answer what
 * the visitor is doing right now, in the style of the first-order bubble
 * (assets/nudge.js):
 *
 *  - size:      on a product with sizes, after 3 size changes without adding,
 *               or 45 s with the sizes seen and none chosen. Offers the size
 *               calculator (if not used yet) and WhatsApp.
 *  - alt:       a sold-out size was tapped and the same design in another
 *               colour has that size: show it under "Avísame cuando vuelva".
 *  - browse:    3rd product page of the visit with nothing added: after 8 s a
 *               bubble on the photo. Two messages tested at random (one per
 *               browser, bst_browse_ab): B free shipping + 14 days to return,
 *               C "¿Te ayudamos a elegir?" with WhatsApp. Event data carries
 *               the variant. A (the category's best seller) was dropped on
 *               2026-10-15: Cami felt it steers taste toward what everyone buys
 *               and sells the same few pieces out; browsers that drew A get
 *               B or C again.
 *  - cart_idle: phones only. Something in the cart and 30 s without touching
 *               the screen: "Tu carrito te espera" + open the cart.
 *
 * They are help, not offers, so both A/B groups of the 10 % test get them the
 * same way (the 10 % comparison stays clean). Each one at most once per visit,
 * and never while another bubble or a dialog is on screen.
 * Events: nudge_shown / nudge_click / nudge_close with { nudge, product }.
 */
(() => {
  if (window.BrisatoNudges) return;
  window.BrisatoNudges = true;

  const cfgEl = document.querySelector('[data-nudges-config]');
  if (!cfgEl) return;
  let cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (_) {
    return;
  }
  const t = cfg.strings || {};

  const track = (name, data = {}) => {
    if (typeof window.brisatoTrack === 'function') window.brisatoTrack(name, data);
  };
  const once = (key) => {
    try {
      if (window.sessionStorage.getItem(key)) return false;
      window.sessionStorage.setItem(key, '1');
    } catch (_) {
      /* private mode: show it, at worst once per page */
    }
    return true;
  };
  const seenAlready = (key) => {
    try {
      return !!window.sessionStorage.getItem(key);
    } catch (_) {
      return false;
    }
  };
  const busy = () => !!document.querySelector('dialog[open], .pp-nudge.is-on, .bn.is-on');
  // Same rule as snippets/title-case.liquid: "Pantalón bestias terra" -> "Pantalón Bestias Terra"
  const SMALL = ',a,al,con,de,del,e,el,en,la,las,los,o,para,por,sin,u,y,and,of,the,';
  const titleCase = (s) => String(s).trim().split(/\s+/).map((w, i) => {
    const lower = w.toLowerCase();
    return i && SMALL.includes(`,${lower},`) ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(' ');
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /** Bubble element: text + optional buttons + × */
  const bubble = (name, html, actions, extraClass = '') => {
    const el = document.createElement('div');
    el.className = `bn ${extraClass}`.trim();
    el.setAttribute('role', 'status');
    el.innerHTML = `<div class="bn__top"><p class="bn__text">${html}</p>`
      + `<button type="button" class="bn__close" aria-label="${esc(t.close || 'Cerrar')}">×</button></div>`
      + (actions.length ? `<div class="bn__actions">${actions.map((a, i) => `<button type="button" class="bn__btn${i ? ' bn__btn--alt' : ''}" data-i="${i}">${esc(a.label)}</button>`).join('')}</div>` : '');
    const hide = () => {
      el.classList.remove('is-on');
      window.setTimeout(() => el.remove(), 400);
    };
    el.querySelector('.bn__close').addEventListener('click', () => {
      track('nudge_close', { nudge: name, product: cfg.product || '' });
      hide();
    });
    el.querySelectorAll('.bn__btn').forEach((b) => b.addEventListener('click', () => {
      const a = actions[Number(b.dataset.i)];
      track('nudge_click', { nudge: name, action: a.id, product: cfg.product || '' });
      a.run();
      hide();
    }));
    el.hide = hide;
    return el;
  };
  const reveal = (el, name) => {
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-on')));
    track('nudge_shown', { nudge: name, product: cfg.product || '' });
  };

  const pp = document.querySelector('product-page:not(.qa)');

  /* ---------- Size doubt ---------- */
  const sizeSet = pp && pp.querySelector('[data-option][data-is-size="true"]');
  if (sizeSet && cfg.productAvailable && !seenAlready('bst_nudge_size')) {
    const start = Date.now();
    let changes = 0;
    let added = false;
    let helperUsed = false;
    let sizesSeen = false;
    let done = false;

    const show = () => {
      if (done || added || busy()) return;
      if (!once('bst_nudge_size')) {
        done = true;
        return;
      }
      done = true;
      const actions = [];
      const toggle = pp.querySelector('[data-size-guide-toggle]');
      if (toggle && !helperUsed) {
        actions.push({ id: 'size_helper', label: t.size_calc, run: () => toggle.click() });
      }
      const wa = pp.querySelector('[data-whatsapp]');
      if (wa) {
        actions.push({ id: 'whatsapp', label: t.size_whatsapp, run: () => window.open(wa.href, '_blank', 'noopener') });
      }
      if (!actions.length) return;
      const el = bubble('size', t.size_q_html, actions, 'bn--inline');
      sizeSet.after(el);
      reveal(el, 'size');
    };

    pp.addEventListener('change', (e) => {
      if (!e.target.closest('[data-is-size="true"]') || e.target.hasAttribute('data-soldout')) return;
      changes += 1;
      if (changes >= 3) window.setTimeout(show, 1200);
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-size-guide-toggle]')) helperUsed = true;
    });
    document.addEventListener('cart:item-added', () => {
      added = true;
    });
    new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) sizesSeen = true;
    }, { threshold: 0.8 }).observe(sizeSet);
    const timer = window.setInterval(() => {
      if (done) {
        window.clearInterval(timer);
        return;
      }
      const chosen = sizeSet.querySelector('input:checked:not([data-soldout])');
      if (Date.now() - start >= 45000 && sizesSeen && !chosen && !helperUsed && document.visibilityState === 'visible') show();
    }, 3000);
  }

  /* ---------- Sold-out size: same design in another colour ---------- */
  if (pp && cfg.title) {
    const box = pp.querySelector('[data-restock]');
    const words = cfg.title.trim().split(/\s+/);
    const norm = (s) => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const gender = (title) => {
      const last = norm(title.trim().split(/\s+/).pop());
      return last === 'hombre' || last === 'mujer' ? last : '';
    };
    const family = norm(words.slice(0, 2).join(' '));
    const myGender = gender(cfg.title);
    let siblings = null;
    const cache = {};

    const findSiblings = async () => {
      if (siblings) return siblings;
      const q = encodeURIComponent(words.slice(0, 2).join(' '));
      try {
        const res = await fetch(`/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`);
        const data = await res.json();
        siblings = (data.resources.results.products || []).filter((p) =>
          p.handle !== cfg.product && norm(p.title).startsWith(`${family} `) && gender(p.title) === myGender && p.available);
      } catch (_) {
        siblings = [];
      }
      return siblings;
    };
    const productJs = async (p) => {
      if (!cache[p.handle]) {
        cache[p.handle] = fetch(`${p.url.split('?')[0]}.js`).then((r) => r.json()).catch(() => null);
      }
      return cache[p.handle];
    };

    let slot = null;
    let token = 0;
    const clear = () => {
      if (slot) slot.remove();
      slot = null;
    };

    pp.addEventListener('change', async (e) => {
      const input = e.target.closest('input[type="radio"]');
      if (!input || !box) return;
      const my = ++token;
      if (!input.hasAttribute('data-soldout')) {
        clear();
        return;
      }
      const size = input.dataset.value;
      const list = await findSiblings();
      const hits = [];
      for (const p of list.slice(0, 4)) {
        const full = await productJs(p);
        const v = full && full.variants.find((x) => x.available && x.options.length === 1 && norm(x.option1) === norm(size));
        if (v) hits.push({ p, v });
        if (hits.length === 2) break;
      }
      if (my !== token || !hits.length) return;
      clear();
      slot = document.createElement('div');
      slot.className = 'bn-alt';
      slot.innerHTML = `<p class="bn-alt__title">${esc(t.alt_title.replace('SIZE', size))}</p>`
        + hits.map(({ p, v }) => {
          const img = p.image ? `<img src="${esc(p.image + (p.image.includes('?') ? '&' : '?') + 'width=120')}" alt="" width="48" height="60">` : '';
          return `<a class="bn-alt__item" href="${esc(p.url.split('?')[0])}?variant=${v.id}" data-alt="${esc(p.handle)}">${img}<span>${esc(titleCase(p.title))}</span><span class="bn-alt__go">${esc(t.alt_view)}</span></a>`;
        }).join('');
      slot.addEventListener('click', (ev) => {
        const a = ev.target.closest('[data-alt]');
        if (a) track('nudge_click', { nudge: 'alt', action: a.dataset.alt, product: cfg.product, size });
      });
      box.prepend(slot);
      track('nudge_shown', { nudge: 'alt', product: cfg.product, size, alt: hits.map((h) => h.p.handle).join(',') });
    });
  }

  /* ---------- Browsing several products without adding ---------- */
  const media = pp && pp.querySelector('.pp-media');
  const added = () => {
    try {
      return !!window.sessionStorage.getItem('bst_added');
    } catch (_) {
      return false;
    }
  };
  document.addEventListener('cart:item-added', () => {
    try {
      window.sessionStorage.setItem('bst_added', '1');
    } catch (_) {
      /* ignore */
    }
  });
  let views = [];
  try {
    views = JSON.parse(window.sessionStorage.getItem('bst_pdp_views') || '[]');
    if (cfg.product && !views.includes(cfg.product)) {
      views.push(cfg.product);
      window.sessionStorage.setItem('bst_pdp_views', JSON.stringify(views.slice(-20)));
    }
  } catch (_) {
    views = [];
  }
  if (media && cfg.product && views.length >= 3 && !added() && Number(cfg.cartCount || 0) === 0 && !seenAlready('bst_nudge_browse')) {
    let variant = null;
    try {
      variant = window.localStorage.getItem('bst_browse_ab');
      if (!['B', 'C'].includes(variant)) {
        variant = ['B', 'C'][Math.floor(Math.random() * 2)];
        window.localStorage.setItem('bst_browse_ab', variant);
      }
    } catch (_) {
      variant = ['B', 'C'][Math.floor(Math.random() * 2)];
    }
    const name = `browse_${variant}`;
    const place = (el) => {
      const box = media.getBoundingClientRect();
      const hidden = Math.max(0, box.bottom - window.innerHeight);
      el.style.bottom = hidden > 0 ? `${hidden + 16}px` : '';
    };

    let shown = false;
    const tryShow = async () => {
      if (shown || added() || document.visibilityState !== 'visible' || busy()) return;
      const r = media.getBoundingClientRect();
      if (r.bottom < 120 || r.top > window.innerHeight - 120) return; // photo not on screen
      shown = true;
      if (!once('bst_nudge_browse')) return;
      let el;
      let used = name;
      if (variant === 'C') {
        const wa = pp.querySelector('[data-whatsapp]');
        if (wa && wa.dataset.number) {
          const href = `https://wa.me/${wa.dataset.number}?text=${encodeURIComponent(t.browse_c_msg)}`;
          el = bubble(name, t.browse_c_html, [{ id: 'whatsapp', label: t.browse_c_btn, run: () => window.open(href, '_blank', 'noopener') }], 'bn--photo');
        }
      }
      if (!el) {
        used = variant === 'B' ? name : `${name}_fallback_B`; // C without a WhatsApp number
        el = bubble(used, t.browse_b_html, [], 'bn--photo');
      }
      media.append(el);
      place(el);
      window.addEventListener('resize', () => place(el), { passive: true });
      reveal(el, used);
    };
    window.setTimeout(() => {
      tryShow();
      const again = window.setInterval(() => (shown ? window.clearInterval(again) : tryShow()), 2000);
    }, 8000);
  }

  /* ---------- Phones: idle with something in the cart ---------- */
  const phone = window.matchMedia('(hover: none) and (max-width: 989px)').matches;
  if (phone && !/^\/(cart|checkouts?)\b/.test(window.location.pathname) && !seenAlready('bst_nudge_cart')) {
    const IDLE = 30000;
    let last = Date.now();
    let done = false;
    const items = () => {
      const c = document.querySelector('[data-cart-content]');
      return c ? Number(c.dataset.count || 0) : Number(cfg.cartCount || 0);
    };
    const poke = () => {
      last = Date.now();
    };
    ['touchstart', 'scroll', 'keydown', 'click'].forEach((ev) => window.addEventListener(ev, poke, { passive: true }));
    const timer = window.setInterval(() => {
      if (done) return;
      if (Date.now() - last < IDLE || document.visibilityState !== 'visible' || items() < 1 || busy()) return;
      if (!once('bst_nudge_cart')) {
        done = true;
        return;
      }
      done = true;
      window.clearInterval(timer);
      const drawer = document.querySelector('cart-drawer');
      const el = bubble('cart_idle', t.cart_idle_html, [{
        id: 'open_cart',
        label: t.cart_view,
        run: () => (drawer && drawer.open ? drawer.open() : (window.location.href = '/cart')),
      }], 'bn--float');
      const sticky = document.querySelector('.pp-sticky.is-visible');
      if (sticky) el.style.bottom = `${sticky.offsetHeight + 12}px`;
      document.body.append(el);
      reveal(el, 'cart_idle');
      window.setTimeout(() => el.isConnected && el.hide(), 15000);
    }, 2000);
  }
})();
